import type { ReactNode } from "react";
import { View } from "react-native";
import type { StyleProp, ViewStyle } from "react-native";
import type { LucideIcon } from "lucide-react-native";
import { SectionTitle } from "./SectionTitle";
import { SectionActionLink } from "./SectionActionLink";

export type SectionTitleRowAction = {
  label: string;
  onPress: () => void;
  accessibilityLabel?: string;
  icon?: LucideIcon;
  showChevron?: boolean;
  disabled?: boolean;
};

type Props = {
  title: string;
  icon?: LucideIcon;
  action?: SectionTitleRowAction;
  /** Optional inline chip/content next to the title, e.g. partner data. */
  afterTitle?: ReactNode;
  /** Optional fully custom right-side content. Takes precedence over `action`. */
  right?: ReactNode;
  style?: StyleProp<ViewStyle>;
  titleGroupStyle?: StyleProp<ViewStyle>;
};

/**
 * Shared section header row: `<SectionTitle>` on the left and an optional
 * standardized action on the right.
 */
export function SectionTitleRow({
  title,
  icon,
  action,
  afterTitle,
  right,
  style,
  titleGroupStyle,
}: Props) {
  return (
    <View
      style={[
        {
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 10,
          paddingHorizontal: 4,
        },
        style,
      ]}
    >
      <View
        style={[
          {
            flexDirection: "row",
            alignItems: "center",
            gap: 8,
            flex: 1,
            flexWrap: "wrap",
            minWidth: 0,
          },
          titleGroupStyle,
        ]}
      >
        <SectionTitle icon={icon}>{title}</SectionTitle>
        {afterTitle}
      </View>
      {right ??
        (action ? (
          <SectionActionLink
            label={action.label}
            onPress={action.onPress}
            accessibilityLabel={action.accessibilityLabel}
            icon={action.icon}
            showChevron={action.showChevron}
            disabled={action.disabled}
          />
        ) : null)}
    </View>
  );
}

export default SectionTitleRow;
